import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import QRCode from "react-qr-code";
import { X, ArrowLeft, ArrowRight, Copy, Check, Home, Building2, BadgeCheck, PartyPopper, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ALL_ITEMS, SLOT_TIMES } from "@/data/catalog";
import { api, buildUpiLink, UPI_ID, formatApiError } from "@/lib/api";

const STEPS = ["Tests", "Details", "Slot", "Payment"];

export default function BookingModal({ open, initialItems, onClose }) {
  const [step, setStep] = useState(0);
  const [items, setItems] = useState([]);
  const [patient, setPatient] = useState({ name: "", phone: "", email: "", age: "", gender: "" });
  const [slot, setSlot] = useState({ date: "", time: "" });
  const [home, setHome] = useState(false);
  const [address, setAddress] = useState("");
  const [utr, setUtr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [booking, setBooking] = useState(null);
  const [copied, setCopied] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [family, setFamily] = useState([]);
  const [bookedFor, setBookedFor] = useState("");

  useEffect(() => {
    if (open) {
      setItems(initialItems?.length ? initialItems : []);
      setStep(0);
      setBooking(null);
      setUtr("");
      setBookedFor("");
      (async () => {
        try {
          const me = await api.get("/auth/customer/me");
          setCustomer(me.data);
          setPatient((p) => ({ ...p, phone: p.phone || me.data.phone, name: p.name || me.data.name || "" }));
          const fam = await api.get("/customer/family");
          setFamily(fam.data || []);
        } catch {
          setCustomer(null);
          setFamily([]);
        }
      })();
    }
  }, [open, initialItems]);

  const pickMember = (m) => {
    if (!m) {
      setBookedFor("");
      setPatient((p) => ({ ...p, name: customer?.name || "", age: "", gender: "" }));
      return;
    }
    setBookedFor(`${m.name} (${m.relation})`);
    setPatient((p) => ({ ...p, name: m.name, age: m.age ? String(m.age) : "", gender: m.gender || "" }));
  };

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const selectedItems = useMemo(() => ALL_ITEMS.filter((i) => items.includes(i.id)), [items]);
  const total = selectedItems.reduce((s, i) => s + i.price, 0);
  const upiLink = buildUpiLink(total, `Hemozy ${patient.name || "booking"}`);

  const minDate = new Date().toISOString().split("T")[0];

  const toggleItem = (id) => setItems((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const validateStep = () => {
    if (step === 0 && items.length === 0) { toast.error("Add at least one test or package"); return false; }
    if (step === 1) {
      if (!patient.name.trim()) { toast.error("Please enter patient name"); return false; }
      if (!/^\d{10}$/.test(patient.phone)) { toast.error("Enter a valid 10-digit phone number"); return false; }
    }
    if (step === 2) {
      if (!slot.date) { toast.error("Pick a date"); return false; }
      if (!slot.time) { toast.error("Pick a time slot"); return false; }
      if (home && !address.trim()) { toast.error("Enter address for home collection"); return false; }
    }
    return true;
  };

  const next = () => { if (validateStep()) setStep((s) => Math.min(s + 1, 3)); };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const copyUpi = async () => {
    try { await navigator.clipboard.writeText(UPI_ID); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch {}
  };

  const submit = async () => {
    if (!utr.trim()) { toast.error("Enter the UTR / transaction reference after paying"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        items,
        patient: { ...patient, age: patient.age ? Number(patient.age) : undefined, email: patient.email || undefined, gender: patient.gender || undefined },
        slot_date: slot.date,
        slot_time: slot.time,
        home_collection: home,
        address: home ? address : undefined,
        utr: utr.trim(),
        booked_for: bookedFor || undefined,
      });
      setBooking(data);
      toast.success("Booking confirmed!");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-[#0B192C]/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
        onClick={onClose} data-testid="booking-modal">
        <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-[2rem] sm:rounded-[2rem] shadow-2xl">
          {/* Header */}
          <div className="sticky top-0 bg-white/90 backdrop-blur-xl border-b border-slate-100 px-6 py-4 flex items-center justify-between z-10">
            <div className="flex items-center gap-3">
              <span className="font-extrabold text-lg text-[#0B192C]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Book Appointment</span>
              {!booking && (
                <div className="hidden sm:flex items-center gap-1.5">
                  {STEPS.map((s, i) => (
                    <div key={s} className={`flex items-center gap-1.5 ${i <= step ? "text-[#0D9488]" : "text-slate-300"}`}>
                      <span className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center ${i <= step ? "bg-[#0D9488] text-white" : "bg-slate-100 text-slate-400"}`}>{i + 1}</span>
                      <span className="text-xs font-medium">{s}</span>
                      {i < 3 && <span className="w-4 h-px bg-slate-200" />}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button onClick={onClose} aria-label="Close" data-testid="booking-close-button"
              className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6">
            {booking ? (
              <Confirmation booking={booking} onClose={onClose} />
            ) : (
              <>
                {step === 0 && (
                  <div>
                    <h3 className="font-bold text-[#0B192C] text-lg mb-1">What do you need tested?</h3>
                    <p className="text-sm text-slate-500 mb-4">Add or remove tests & packages.</p>
                    <div className="max-h-[46vh] overflow-y-auto space-y-2 pr-1">
                      {ALL_ITEMS.map((item) => {
                        const on = items.includes(item.id);
                        return (
                          <button key={item.id} onClick={() => toggleItem(item.id)} data-testid={`booking-item-${item.id}`}
                            className={`w-full flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left transition-all duration-150 ${on ? "border-[#0D9488] bg-[#0D9488]/[0.05]" : "border-slate-200 hover:border-slate-300"}`}>
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-[#0B192C] truncate">{item.name}</div>
                              <div className="text-xs text-slate-500">TAT: {item.tat}</div>
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                              <span className="font-mono-med text-sm font-semibold text-[#0B192C]">₹{item.price.toLocaleString("en-IN")}</span>
                              <span className={`w-6 h-6 rounded-full flex items-center justify-center ${on ? "bg-[#0D9488] text-white" : "border border-slate-300 text-transparent"}`}>
                                <Check className="w-3.5 h-3.5" />
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4">
                      <span className="text-sm text-slate-500">{items.length} selected</span>
                      <span className="font-mono-med text-xl font-bold text-[#0B192C]">₹{total.toLocaleString("en-IN")}</span>
                    </div>
                  </div>
                )}

                {step === 1 && (
                  <div>
                    <h3 className="font-bold text-[#0B192C] text-lg mb-1">Patient details</h3>
                    <p className="text-sm text-slate-500 mb-5">Reports and updates go to this number.</p>
                    {customer && (
                      <div className="mb-5" data-testid="family-picker">
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Who is this booking for?</label>
                        <div className="mt-2 flex flex-wrap gap-2">
                          <button onClick={() => pickMember(null)} data-testid="family-chip-myself"
                            className={`text-xs font-semibold px-4 py-2 rounded-full border transition-all active:scale-95 ${!bookedFor ? "bg-[#0B192C] text-white border-[#0B192C]" : "bg-white text-slate-600 border-slate-200 hover:border-[#0D9488]"}`}>
                            Myself
                          </button>
                          {family.map((m) => (
                            <button key={m.id} onClick={() => pickMember(m)} data-testid={`family-chip-${m.id}`}
                              className={`text-xs font-semibold px-4 py-2 rounded-full border transition-all active:scale-95 ${bookedFor === `${m.name} (${m.relation})` ? "bg-[#0D9488] text-white border-[#0D9488]" : "bg-white text-slate-600 border-slate-200 hover:border-[#0D9488]"}`}>
                              {m.name} · {m.relation}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="sm:col-span-2">
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Full name *</label>
                        <input value={patient.name} onChange={(e) => setPatient({ ...patient, name: e.target.value })} data-testid="booking-name-input"
                          className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]/40 focus:border-[#0D9488]" placeholder="Patient's full name" />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Phone (10 digits) *</label>
                        <input value={patient.phone} onChange={(e) => setPatient({ ...patient, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} inputMode="numeric" data-testid="booking-phone-input"
                          className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]/40 focus:border-[#0D9488]" placeholder="98XXXXXXXX" />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Email (optional)</label>
                        <input value={patient.email} onChange={(e) => setPatient({ ...patient, email: e.target.value })} type="email" data-testid="booking-email-input"
                          className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]/40 focus:border-[#0D9488]" placeholder="you@email.com" />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Age</label>
                        <input value={patient.age} onChange={(e) => setPatient({ ...patient, age: e.target.value.replace(/\D/g, "").slice(0, 3) })} inputMode="numeric" data-testid="booking-age-input"
                          className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]/40 focus:border-[#0D9488]" placeholder="35" />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Gender</label>
                        <select value={patient.gender} onChange={(e) => setPatient({ ...patient, gender: e.target.value })} data-testid="booking-gender-select"
                          className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0D9488]/40 focus:border-[#0D9488]">
                          <option value="">Select</option>
                          <option value="female">Female</option>
                          <option value="male">Male</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div>
                    <h3 className="font-bold text-[#0B192C] text-lg mb-1">Pick your slot</h3>
                    <p className="text-sm text-slate-500 mb-5">Visit us in MVP Colony, or we'll come to you.</p>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Date *</label>
                        <input type="date" min={minDate} value={slot.date} onChange={(e) => setSlot({ ...slot, date: e.target.value })} data-testid="booking-date-input"
                          className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]/40 focus:border-[#0D9488]" />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Time *</label>
                        <select value={slot.time} onChange={(e) => setSlot({ ...slot, time: e.target.value })} data-testid="booking-time-select"
                          className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0D9488]/40 focus:border-[#0D9488]">
                          <option value="">Select time</option>
                          {SLOT_TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="mt-5 grid grid-cols-2 gap-3">
                      <button onClick={() => setHome(false)} data-testid="booking-visit-center"
                        className={`flex items-center gap-3 rounded-xl border p-4 text-left transition-all ${!home ? "border-[#0D9488] bg-[#0D9488]/[0.05] ring-1 ring-[#0D9488]/30" : "border-slate-200 hover:border-slate-300"}`}>
                        <Building2 className="w-5 h-5 text-[#0B192C] shrink-0" />
                        <div>
                          <div className="text-sm font-semibold text-[#0B192C]">Visit center</div>
                          <div className="text-xs text-slate-500">MVP Colony</div>
                        </div>
                      </button>
                      <button onClick={() => setHome(true)} data-testid="booking-home-collection"
                        className={`flex items-center gap-3 rounded-xl border p-4 text-left transition-all ${home ? "border-[#0D9488] bg-[#0D9488]/[0.05] ring-1 ring-[#0D9488]/30" : "border-slate-200 hover:border-slate-300"}`}>
                        <Home className="w-5 h-5 text-[#D92638] shrink-0" />
                        <div>
                          <div className="text-sm font-semibold text-[#0B192C]">Home collection</div>
                          <div className="text-xs text-slate-500">Free within Vizag</div>
                        </div>
                      </button>
                    </div>
                    {home && (
                      <div className="mt-4">
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Collection address *</label>
                        <textarea value={address} onChange={(e) => setAddress(e.target.value)} rows={2} data-testid="booking-address-input"
                          className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]/40 focus:border-[#0D9488]" placeholder="Flat / house no, street, area, Visakhapatnam" />
                      </div>
                    )}
                  </div>
                )}

                {step === 3 && (
                  <div>
                    <h3 className="font-bold text-[#0B192C] text-lg mb-1">Pay by UPI</h3>
                    <p className="text-sm text-slate-500 mb-5">Scan with GPay, PhonePe, Paytm or any UPI app.</p>
                    <div className="grid sm:grid-cols-2 gap-6 items-center">
                      <div className="flex flex-col items-center">
                        <div className="bg-white border-2 border-[#0B192C] rounded-2xl p-4 shadow-sm" data-testid="upi-qr-code">
                          <QRCode value={upiLink} size={170} />
                        </div>
                        <button onClick={copyUpi} data-testid="copy-upi-button"
                          className="mt-3 flex items-center gap-2 text-xs font-medium text-slate-600 border border-slate-200 rounded-full px-4 py-2 hover:border-[#0D9488] hover:text-[#0D9488] transition-colors">
                          {copied ? <Check className="w-3.5 h-3.5 text-[#0D9488]" /> : <Copy className="w-3.5 h-3.5" />}
                          {copied ? "Copied!" : UPI_ID}
                        </button>
                      </div>
                      <div>
                        <div className="bg-[#F8FAFC] border border-slate-200 rounded-2xl p-5 space-y-2.5">
                          {selectedItems.map((i) => (
                            <div key={i.id} className="flex justify-between text-sm">
                              <span className="text-slate-600 truncate pr-3">{i.name}</span>
                              <span className="font-mono-med text-slate-800 shrink-0">₹{i.price.toLocaleString("en-IN")}</span>
                            </div>
                          ))}
                          <div className="border-t border-slate-200 pt-2.5 flex justify-between">
                            <span className="font-semibold text-[#0B192C] text-sm">Total</span>
                            <span className="font-mono-med font-bold text-lg text-[#D92638]" data-testid="booking-total">₹{total.toLocaleString("en-IN")}</span>
                          </div>
                        </div>
                        <div className="mt-4">
                          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">UTR / Transaction reference *</label>
                          <input value={utr} onChange={(e) => setUtr(e.target.value)} data-testid="booking-utr-input"
                            className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-mono-med focus:outline-none focus:ring-2 focus:ring-[#0D9488]/40 focus:border-[#0D9488]" placeholder="e.g. 412345678901" />
                          <p className="mt-2 text-xs text-slate-400">Found in your UPI app after payment. We'll verify and confirm on WhatsApp.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Footer nav */}
                <div className="mt-7 flex items-center justify-between border-t border-slate-100 pt-5">
                  <button onClick={back} disabled={step === 0} data-testid="booking-back-button"
                    className="flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-[#0B192C] disabled:opacity-30 disabled:hover:text-slate-500 transition-colors">
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                  {step < 3 ? (
                    <button onClick={next} data-testid="booking-next-button"
                      className="flex items-center gap-2 bg-[#0B192C] hover:bg-[#1E3E62] text-white text-sm font-semibold px-6 py-3 rounded-full transition-all duration-200 active:scale-95">
                      Continue <ArrowRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button onClick={submit} disabled={submitting} data-testid="confirm-payment-button"
                      className="flex items-center gap-2 bg-[#D92638] hover:bg-[#B81D2D] text-white text-sm font-semibold px-6 py-3 rounded-full transition-all duration-200 active:scale-95 disabled:opacity-60">
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <BadgeCheck className="w-4 h-4" />}
                      {submitting ? "Confirming..." : `Confirm booking - ₹${total.toLocaleString("en-IN")}`}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function Confirmation({ booking, onClose }) {
  return (
    <div className="text-center py-6" data-testid="booking-confirmation">
      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 14 }}
        className="w-16 h-16 mx-auto rounded-full bg-[#0D9488]/10 flex items-center justify-center">
        <PartyPopper className="w-7 h-7 text-[#0D9488]" />
      </motion.div>
      <h3 className="mt-5 text-2xl font-bold text-[#0B192C]">Booking received!</h3>
      <p className="mt-2 text-sm text-slate-500 max-w-sm mx-auto">
        We'll verify your payment and confirm your slot on WhatsApp shortly. Save your reference ID.
      </p>
      <div className="mt-6 inline-block bg-[#0B192C] rounded-2xl px-8 py-4">
        <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-slate-400">Reference ID</div>
        <div className="font-mono-med text-2xl font-bold text-[#14B8A6]" data-testid="booking-ref">{booking.ref}</div>
      </div>
      <div className="mt-6 bg-[#F8FAFC] border border-slate-200 rounded-2xl p-5 text-left max-w-md mx-auto space-y-2 text-sm">
        <div className="flex justify-between"><span className="text-slate-500">Patient</span><span className="font-medium text-[#0B192C]">{booking.patient.name}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">Slot</span><span className="font-medium text-[#0B192C]">{booking.slot_date} · {booking.slot_time}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">Collection</span><span className="font-medium text-[#0B192C]">{booking.home_collection ? "Home collection" : "Center visit"}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">Amount</span><span className="font-mono-med font-semibold text-[#0B192C]">₹{booking.total.toLocaleString("en-IN")}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">Status</span><span className="font-semibold text-amber-600">Payment verification pending</span></div>
      </div>
      <button onClick={onClose} data-testid="confirmation-done-button"
        className="mt-7 bg-[#0B192C] hover:bg-[#1E3E62] text-white text-sm font-semibold px-8 py-3 rounded-full transition-all duration-200 active:scale-95">
        Done
      </button>
    </div>
  );
}
